// client/src/components/media/PostDetail.jsx
// Detail page for a single Timeline post (an event_item under the Timeline event).
// Modeled on EventItemDetail but scoped to post-style display + action setup.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import AppFooter from "../AppFooter";
import { FaArrowLeft, FaBolt, FaSave, FaTrash } from "react-icons/fa";
import ActionTypePicker from "./ActionTypePicker";
import { sessionRequestConfig } from "../../services/sessionLocal";

import AuctionConfig from "../event/AuctionConfig";
import RaffleConfig from "../event/RaffleConfig";
import ChallengeConfig from "../event/ChallengeConfig";
import ContestConfig from "../event/ContestConfig";
import AMAConfig from "../event/AMAConfig";
import UGCCampaignConfig from "../event/UGCCampaignConfig";
import ThemedDayConfig from "../event/ThemedDayConfig";
import CollabTakeoverConfig from "../event/CollabTakeoverConfig";
import PollQuizConfig from "../event/PollQuizConfig";

const toAbs = (baseUrl, p) =>
  !p ? p : /^https?:\/\//i.test(p) ? p : `${baseUrl}${p.startsWith("/") ? "" : "/"}${p}`;

const isVideoMedia = (m) =>
  (m?.mimetype?.startsWith?.("video/")) || /\.(mp4|mov|webm|ogg|avi|mkv)$/i.test(m?.path || m?.url || "");

const ACTION_LABELS = {
  auction: "Auction",
  raffle: "Raffle",
  challenge: "Challenge",
  contest: "Contest",
  ama: "AMA",
  ugc_campaign: "UGC Campaign",
  themed_day: "Themed Day",
  collab_takeover: "Collab / Takeover",
  poll_quiz: "Poll / Quiz",
};

const formatDateTime = (s) => {
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
};

const PostDetail = () => {
  const { tenant, postId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const baseUrl = process.env.REACT_APP_BASE_URL || (typeof window !== "undefined" ? window.location.origin : "http://localhost:5000");
  const cfg = sessionRequestConfig();

  const [timelineEventId, setTimelineEventId] = useState(null);
  const [item, setItem] = useState(null);
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [currentAction, setCurrentAction] = useState(null);

  const refreshCurrentAction = async (eventId) => {
    const evId = eventId || timelineEventId;
    if (!evId || !postId) return;
    try {
      const { data } = await axios.get(
        `/api/events/${tenant}/events/${evId}/items/${postId}/action`,
        cfg
      );
      setCurrentAction(data?.action || null);
    } catch {
      /* leave previous value */
    }
  };
  const closeConfig = (setter) => () => {
    setter(false);
    refreshCurrentAction();
  };

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [savingMeta, setSavingMeta] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [pickerOpen, setPickerOpen] = useState(false);

  const [showAuctionConfig, setShowAuctionConfig] = useState(false);
  const [showRaffleConfig, setShowRaffleConfig] = useState(false);
  const [showChallengeConfig, setShowChallengeConfig] = useState(false);
  const [showContestConfig, setShowContestConfig] = useState(false);
  const [showAMAConfig, setShowAMAConfig] = useState(false);
  const [showUGCCampaignConfig, setShowUGCCampaignConfig] = useState(false);
  const [showThemedDayConfig, setShowThemedDayConfig] = useState(false);
  const [showCollabTakeoverConfig, setShowCollabTakeoverConfig] = useState(false);
  const [showPollQuizConfig, setShowPollQuizConfig] = useState(false);

  const actionConfigMap = useMemo(() => ({
    auction: setShowAuctionConfig,
    raffle: setShowRaffleConfig,
    challenge: setShowChallengeConfig,
    contest: setShowContestConfig,
    ama: setShowAMAConfig,
    ugc_campaign: setShowUGCCampaignConfig,
    themed_day: setShowThemedDayConfig,
    collab_takeover: setShowCollabTakeoverConfig,
    poll_quiz: setShowPollQuizConfig,
  }), []);

  // Resolve Timeline event + load this post (item) + its media + any attached action
  useEffect(() => {
    if (!tenant || !postId) return;
    let alive = true;

    (async () => {
      setLoading(true);
      try {
        const { data: timelineData } = await axios.get(`/api/events/${tenant}/timeline/ensure`, cfg);
        const eventId = timelineData?.event?.id;
        if (!eventId) throw new Error("Timeline event not found");
        if (!alive) return;
        setTimelineEventId(eventId);

        const [itemRes, imagesRes, videosRes] = await Promise.all([
          axios.get(`/api/events/${tenant}/events/${eventId}/items/${postId}`, cfg),
          axios.get(`/api/events/${tenant}/events/${eventId}/items/${postId}/images`, cfg).catch(() => ({ data: { images: [] } })),
          axios.get(`/api/${tenant}/events/${eventId}/items/${postId}/videos`, cfg).catch(() => ({ data: { videos: [] } })),
        ]);
        if (!alive) return;

        const it = itemRes.data?.item;
        if (!it) throw new Error("Post not found");
        setItem(it);
        setName(it.name || "");
        setDescription(it.description || "");
        setImages(imagesRes.data?.images || []);
        setVideos(videosRes.data?.videos || []);

        refreshCurrentAction(eventId);
      } catch (err) {
        console.error("PostDetail load error:", err);
        if (alive) setError(err?.response?.data?.message || err?.message || "Failed to load post");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant, postId]);

  // Auto-open an action config when arriving with ?openAction=<type>
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requested = (params.get("openAction") || "").toLowerCase();
    if (!requested) return;
    const setter = actionConfigMap[requested];
    if (!setter) return;
    setter(true);
    params.delete("openAction");
    const next = params.toString();
    navigate(
      { pathname: location.pathname, search: next ? `?${next}` : "" },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handlePickerConfirm = ({ type }) => {
    setPickerOpen(false);
    const setter = actionConfigMap[type];
    if (setter) setter(true);
  };

  const handleSaveMeta = async () => {
    if (!timelineEventId || !item) return;
    try {
      setSavingMeta(true);
      await axios.put(
        `/api/events/${tenant}/events/${timelineEventId}/items/${postId}`,
        { name: name.trim() || item.name, description, type: item.type || "default" },
        cfg
      );
      setItem((prev) => prev ? { ...prev, name: name.trim() || prev.name, description } : prev);
    } catch (e) {
      console.error("Save post failed:", e);
      alert("Could not save post: " + (e?.response?.data?.message || e?.message));
    } finally {
      setSavingMeta(false);
    }
  };

  const handleDelete = async () => {
    if (!timelineEventId) return;
    if (!window.confirm("Delete this post? This cannot be undone.")) return;
    try {
      await axios.delete(
        `/api/events/${tenant}/events/${timelineEventId}/items/${postId}`,
        cfg
      );
      navigate(`/${tenant}/post`);
    } catch (e) {
      console.error("Delete post failed:", e);
      alert("Could not delete post: " + (e?.response?.data?.message || e?.message));
    }
  };

  const footerButtons = [
    { id: "back", icon: <FaArrowLeft />, label: "Back", onClick: () => navigate(`/${tenant}/post`), disabled: loading },
    { id: "action", icon: <FaBolt />, label: currentAction ? "Change Action" : "Add Action", onClick: () => setPickerOpen(true), disabled: loading || !item },
    { id: "save", icon: <FaSave />, label: "Save", onClick: handleSaveMeta, disabled: loading || savingMeta || !item },
    { id: "delete", icon: <FaTrash />, label: "Delete", onClick: handleDelete, disabled: loading || !item },
  ];

  if (loading) {
    return (
      <div className="container">
        <h2 className="hdr hdr--primary hdr--lg">Post</h2>
        <div>Loading post...</div>
        <AppFooter buttons={footerButtons} />
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="container">
        <h2 className="hdr hdr--primary hdr--lg">Post</h2>
        <div className="alert alert-danger">{error || "Post not found"}</div>
        <AppFooter buttons={footerButtons} />
      </div>
    );
  }

  const actionLabel = currentAction ? (ACTION_LABELS[currentAction] || currentAction) : null;

  return (
    <div className="container-fluid">
      <div className="flex items-center justify-between mb-3">
        <h3 className="m-0">Post</h3>
        <div className="small text-gray-400">{formatDateTime(item.createdon)}</div>
      </div>

      <div className="desktop-action-bar">
        <button type="button" onClick={() => navigate(`/${tenant}/post`)}>
          <FaArrowLeft /> Back
        </button>
        <button type="button" onClick={() => setPickerOpen(true)} disabled={!item}>
          <FaBolt /> {currentAction ? "Change Action" : "Add Action"}
        </button>
        <button type="button" className="btn-primary-action" onClick={handleSaveMeta} disabled={savingMeta || !item}>
          <FaSave /> Save
        </button>
        <button type="button" className="btn-danger-action" onClick={handleDelete} disabled={!item}>
          <FaTrash /> Delete
        </button>
      </div>

      {actionLabel && (
        <div className="alert alert-info" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <FaBolt /> Action attached: <strong>{actionLabel}</strong>
        </div>
      )}

      <div className="desktop-two-col">
        <div className="desktop-two-col__form">
          <div className="form-group" style={{ marginBottom: "1rem" }}>
            <label htmlFor="postName">Post name</label>
            <input
              id="postName"
              type="text"
              className="form-control"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
            />
          </div>

          <div className="form-group" style={{ marginBottom: "1rem" }}>
            <label htmlFor="postDescription">Description</label>
            <textarea
              id="postDescription"
              className="form-control"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
            />
          </div>
        </div>

        <div className="desktop-two-col__media">
          {images.length === 0 && videos.length === 0 ? (
            <div className="text-gray-400">No media on this post.</div>
          ) : (
            <>
              {images.map((img) => (
                <div key={img.id || img.path} className="border rounded overflow-hidden" style={{ background: "#111", marginBottom: 10 }}>
                  <img
                    src={toAbs(baseUrl, img.path)}
                    alt={item.name || "post media"}
                    style={{ width: "100%", height: "auto", display: "block", objectFit: "contain" }}
                  />
                </div>
              ))}
              {videos.map((vid) => (
                <div key={vid.id || vid.path} style={{ marginBottom: 10 }}>
                  <video
                    src={toAbs(baseUrl, vid.path)}
                    controls
                    playsInline
                    preload="metadata"
                    style={{ width: "100%", borderRadius: 8 }}
                  >
                    <track kind="captions" />
                  </video>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      <AppFooter buttons={footerButtons} />

      <ActionTypePicker
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onConfirm={handlePickerConfirm}
        initialName={item?.name}
        title={currentAction ? "Change action" : "Add an action"}
      />

      {showAuctionConfig && timelineEventId && (
        <div className="modal-overlay" onClick={closeConfig(setShowAuctionConfig)}>
          <div className="modal-content" style={{ overflow: "auto" }} onClick={(e) => e.stopPropagation()}>
            <AuctionConfig
              tenantId={tenant}
              eventId={timelineEventId}
              itemId={postId}
              itemName={name}
              onClose={closeConfig(setShowAuctionConfig)}
            />
          </div>
        </div>
      )}
      {showRaffleConfig && timelineEventId && (
        <div className="modal-overlay" onClick={closeConfig(setShowRaffleConfig)}>
          <div className="modal-content" style={{ overflow: "auto" }} onClick={(e) => e.stopPropagation()}>
            <RaffleConfig
              tenantId={tenant}
              eventId={timelineEventId}
              itemId={postId}
              itemName={name}
              onClose={closeConfig(setShowRaffleConfig)}
            />
          </div>
        </div>
      )}
      {showChallengeConfig && timelineEventId && (
        <ChallengeConfig
          tenantId={tenant}
          eventId={timelineEventId}
          itemId={postId}
          itemName={name}
          onClose={closeConfig(setShowChallengeConfig)}
        />
      )}
      {showContestConfig && timelineEventId && (
        <ContestConfig
          tenantId={tenant}
          eventId={timelineEventId}
          itemId={postId}
          itemName={name}
          onClose={closeConfig(setShowContestConfig)}
        />
      )}
      {showAMAConfig && timelineEventId && (
        <AMAConfig
          tenantId={tenant}
          eventId={timelineEventId}
          itemId={postId}
          itemName={name}
          onClose={closeConfig(setShowAMAConfig)}
        />
      )}
      {showUGCCampaignConfig && timelineEventId && (
        <UGCCampaignConfig
          tenantId={tenant}
          eventId={timelineEventId}
          itemId={postId}
          itemName={name}
          onClose={closeConfig(setShowUGCCampaignConfig)}
        />
      )}
      {showThemedDayConfig && timelineEventId && (
        <ThemedDayConfig
          tenantId={tenant}
          eventId={timelineEventId}
          itemId={postId}
          itemName={name}
          onClose={closeConfig(setShowThemedDayConfig)}
        />
      )}
      {showCollabTakeoverConfig && timelineEventId && (
        <CollabTakeoverConfig
          tenantId={tenant}
          eventId={timelineEventId}
          itemId={postId}
          itemName={name}
          onClose={closeConfig(setShowCollabTakeoverConfig)}
        />
      )}
      {showPollQuizConfig && timelineEventId && (
        <PollQuizConfig
          tenantId={tenant}
          eventId={timelineEventId}
          itemId={postId}
          itemName={name}
          onClose={closeConfig(setShowPollQuizConfig)}
        />
      )}
    </div>
  );
};

export default PostDetail;
